import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, Router, withRouterConfig } from '@angular/router';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideMarkdown } from 'ngx-markdown';
import { provideTranslateService, provideTranslateCompiler, TranslateLoader } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { MultiFileTranslateLoader } from './common/i18n/multi-file-translate-loader';
import { BUILD_VERSION } from '../environments/build-version';

import { routes } from './app.routes';
import { authInterceptor } from './common/auth/interceptors/auth.interceptor';
import * as Sentry from '@sentry/angular';
import { TrackingCookieService } from './common/analytics/tracking-cookie.service';
import {
  LanguageService,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
} from './common/services/language.service';

const TRANSLATION_MODULES = [
  'common',
  'teacher-dashboard',
  'teacher-assignment-dashboard',
  'teacher-ai-tutor-dashboard',
  'teacher-ai-tutor-wizard',
  'teacher-lesson-plan-dashboard',
  'teacher-lesson-plan-wizard',
  'teacher-grading-dashboard',
  'teacher-assignment-wizard',
  'teacher-grading-wizard',
  'landing',
  'assignment-landing',
  'ai-tutor-landing',
  'grading-landing',
  'lesson-plan-landing',
  'paywall',
  'join-activity',
  'student-dashboard',
  'student-assignment-dashboard',
  'student-ai-tutor-dashboard',
  'student-assignment-wizard',
  'student-ai-tutor-wizard',
];

function detectInitialLanguage(): string {
  const fromQuery = new URLSearchParams(window.location.search).get('lang');
  if (fromQuery && (SUPPORTED_LANGUAGES as readonly string[]).includes(fromQuery)) {
    return fromQuery;
  }

  const fromStorage = localStorage.getItem('jt_lang');
  if (fromStorage && (SUPPORTED_LANGUAGES as readonly string[]).includes(fromStorage)) {
    return fromStorage;
  }

  const browserLocale = navigator.language;
  const browserLang = browserLocale?.split('-')[0];
  const match = (SUPPORTED_LANGUAGES as readonly string[]).find(
    (l) => l === browserLocale || l === `${browserLang}-${browserLocale?.split('-')[1]?.toUpperCase()}`,
  ) ?? (SUPPORTED_LANGUAGES as readonly string[]).find((l) => l.split('-')[0] === browserLang);
  if (match) {
    return match;
  }

  return DEFAULT_LANGUAGE;
}

function createTranslateLoader(http: HttpClient): MultiFileTranslateLoader {
  return new MultiFileTranslateLoader(http, TRANSLATION_MODULES, '/i18n', BUILD_VERSION);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideMarkdown(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
      compiler: provideTranslateCompiler(TranslateMessageFormatCompiler),
      lang: detectInitialLanguage(),
      fallbackLang: 'en',
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (marketingSessionService: TrackingCookieService) => () => {
        marketingSessionService.captureFromUrl();
      },
      deps: [TrackingCookieService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (languageService: LanguageService) => () => {},
      deps: [LanguageService],
      multi: true,
    },
  ],
};
